import { useCallback, useEffect, useRef, useState } from 'react';
import BZAPIService from '../services/BZAPIService';

const RUNNING_INTERVAL_MS = 5000;
const IDLE_INTERVAL_MS = 30000;

// Iteration counts only ever monotonically increase. The BE caches cumulative
// counts per Lambda container, but Lambda can run multiple concurrent containers
// each with its own cache, so a fresh container's first poll for a vendor can
// momentarily under-report relative to a warmer container's view (causing
// 11 -> 10 -> 12 jitter as polls round-robin between containers). Clamp upward
// per executionArn here so the displayed counts are guaranteed monotonic
// regardless of which Lambda container served any given poll. `running`
// fluctuates naturally as iterations start/finish, so it's not clamped.
function applyCumulativeProgress(data, store) {
  if (!data?.hasExecution || !data.progress || !data.executionArn) return data;
  const arn = data.executionArn;
  const prev = store[arn] || { total: 0, completed: 0, failed: 0 };
  const next = {
    total: Math.max(data.progress.total || 0, prev.total),
    completed: Math.max(data.progress.completed || 0, prev.completed),
    failed: Math.max(data.progress.failed || 0, prev.failed),
    running: data.progress.running || 0
  };
  store[arn] = next;
  return { ...data, progress: next };
}

export function useVendorExecutionStatus(vendorId) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);
  const cancelledRef = useRef(false);
  const tickRef = useRef(null);
  const cumulativeProgressRef = useRef({});

  useEffect(() => {
    if (!vendorId) return undefined;
    cancelledRef.current = false;

    async function fetchStatus() {
      try {
        const raw = await BZAPIService.getVendorExecutionStatus(vendorId);
        if (cancelledRef.current) return raw;
        const data = applyCumulativeProgress(raw, cumulativeProgressRef.current);
        setStatus(data);
        return data;
      } catch (e) {
        return null;
      } finally {
        if (!cancelledRef.current) setLoading(false);
      }
    }

    function clearTimer() {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    }

    async function tick() {
      clearTimer();
      const data = await fetchStatus();
      if (cancelledRef.current) return;
      if (document.hidden) return;
      const next = data?.hasExecution && data?.isRunning ? RUNNING_INTERVAL_MS : IDLE_INTERVAL_MS;
      timerRef.current = setTimeout(tick, next);
    }

    tickRef.current = tick;

    function onVisibilityChange() {
      if (document.hidden) {
        clearTimer();
      } else if (!timerRef.current) {
        tick();
      }
    }

    tick();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelledRef.current = true;
      clearTimer();
      tickRef.current = null;
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [vendorId]);

  // Imperative refresh — call after a form action that might immediately
  // change execution state (force-run, service toggle) so the UI doesn't
  // sit on the old status until the next polling tick. Re-runs tick() so
  // the polling cadence (running vs idle) is recomputed against the new
  // result.
  const refresh = useCallback(() => {
    if (tickRef.current) tickRef.current();
  }, []);

  return { status, loading, refresh };
}
